import {
    FormEventHandler,
    ReactElement,
    useCallback,
    useEffect,
    useState
} from 'react';
import { useResetPasswordAction } from '../actions';
import {
    UIButton,
    UIFormInput,
    UIInlineAlert,
    UISpacer,
    UIText,
    UIToast
} from '@/ui';
import styles from './reset-password.module.scss';
import {
    PASSWORD_MAX_LENGTH,
    validatePassword
} from '@/common/util/validators/password';
import { Link } from 'react-router';
import { useMeTranslation } from '../hooks';

type ResetPasswordProps = {
    token: string;
    onSuccess?: () => void;
};

export function ResetPassword(props: ResetPasswordProps): ReactElement {
    const { token, onSuccess } = props;

    const { t } = useMeTranslation();

    const { resetPassword, loading, error, clearError } =
        useResetPasswordAction();

    return (
        <>
            <UIText variant="h2" bold>
                {t('shared.passwords.reset_title')}
            </UIText>
            <UISpacer multiplier={2} />
            {error && (
                <>
                    <UIInlineAlert text={error} onClose={() => clearError()} />
                    <UISpacer multiplier={2} />
                </>
            )}
            <ResetPasswordForm
                loading={loading}
                onSubmit={({ password }) => {
                    resetPassword({
                        password,
                        token
                    })
                        .then(() => {
                            onSuccess?.();
                        })
                        .catch(() => {}); // Handled in the hook
                }}
            />
        </>
    );
}

type ResetPasswordFormProps = {
    onSubmit?: (data: { password: string }) => void;
    loading?: boolean;
};
export function ResetPasswordForm(props: ResetPasswordFormProps): ReactElement {
    const { t } = useMeTranslation();

    const { onSubmit, loading } = props;

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * New password
     */
    const [newPassword, setNewPassword] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const checkNewPassword = useCallback(
        async (password: string): Promise<string> => {
            const error = await validatePassword(password);
            if (error) {
                return t(error);
            }
            return '';
        },
        [t]
    );
    useEffect(() => {
        let isMounted = true;
        if (showRealTimeErrors) {
            checkNewPassword(newPassword)
                .then((response) => {
                    if (isMounted) {
                        setNewPasswordError(response);
                    }
                })
                .catch(() => {
                    if (isMounted) {
                        setNewPasswordError(t('shared.errors.generic'));
                    }
                });
        }

        return () => {
            isMounted = false; // Cleanup function
        };
    }, [newPassword, showRealTimeErrors, checkNewPassword, t]);

    /**
     * Confirm password
     */
    const [confirmPassword, setConfirmPassword] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const checkConfirmPassword = useCallback(
        (confirmPassword: string): string => {
            if (confirmPassword !== newPassword) {
                return t('shared.fields.password.error_no_match');
            }
            return '';
        },
        [t, newPassword]
    );
    useEffect(() => {
        if (showRealTimeErrors) {
            setConfirmPasswordError(checkConfirmPassword(confirmPassword));
        }
    }, [confirmPassword, showRealTimeErrors, checkConfirmPassword]);

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        checkNewPassword(newPassword)
            .then((isNewPasswordValid) => {
                if (
                    !isNewPasswordValid &&
                    !checkConfirmPassword(confirmPassword)
                ) {
                    setShowRealTimeErrors(false);
                    onSubmit?.({ password: newPassword });
                }
            })
            .catch(() => {
                UIToast.error(t('shared.errors.generic'));
            });
    };

    return (
        <div className={styles.container}>
            <form onSubmit={handleSubmit} noValidate>
                <UIFormInput
                    name="new password"
                    label={t('shared.fields.password.new_label')}
                    value={newPassword}
                    type="password"
                    errorText={newPasswordError}
                    required
                    maxLength={PASSWORD_MAX_LENGTH}
                    onChange={setNewPassword}
                />
                <UISpacer multiplier={2} />
                <UIFormInput
                    name="confirm password"
                    label={t('shared.fields.password.confirm_label')}
                    value={confirmPassword}
                    type="password"
                    errorText={confirmPasswordError}
                    required
                    maxLength={PASSWORD_MAX_LENGTH}
                    onChange={setConfirmPassword}
                />
                <UISpacer multiplier={2} />
                <UIButton
                    variant="primary"
                    type="submit"
                    loading={loading}
                    disabled={loading}
                >
                    {t('shared.passwords.reset_submit')}
                </UIButton>
            </form>
        </div>
    );
}

export function ResetPasswordSuccess(): ReactElement {
    const { t } = useMeTranslation();

    return (
        <>
            <UIText variant="h2" bold>
                {t('components.reset_password.success_title')}
            </UIText>
            <UISpacer multiplier={2} />
            <Link to={'/login'} style={{ textDecoration: 'none' }}>
                <UIButton variant="primary">
                    {t('shared.labels.login')}
                </UIButton>
            </Link>
        </>
    );
}
